import React, { useMemo, useState } from "react";
import { exporter } from "@/lib/api";
import SkillVerification from "@/components/SkillVerification";

const STATUS_PILL = {
  full: { cls: "pill-green", label: "✅ Full" },
  partial: { cls: "pill-orange", label: "⚠ Partial" },
  not_started: { cls: "pill-red", label: "❌ None" },
};

const CHANGE_BADGE = {
  new: { t: "🆕 New", c: "pill-green" },
  modified: { t: "✏️ Modified", c: "pill-orange" },
  removed: { t: "🗑 Removed", c: "pill-red" },
};

const covCls = (c) => (c >= 95 ? "cov-full" : c >= 30 ? "cov-partial" : "cov-none");
const covColor = (c) => (c >= 95 ? "var(--success)" : c >= 30 ? "var(--warning)" : "var(--danger)");

// Rendered as a Response Workspace tab panel (same role as the AI Response
// Editor / Team Assignments panels) — takes the already-loaded proposal from
// the parent rather than resolving its own, so it can never show a different
// proposal than the rest of the workspace.
export default function ComplianceTab({ proposal, onEditResponse }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [sectionFilter, setSectionFilter] = useState("all");

  const stats = useMemo(() => {
    const reqs = proposal.requirements || [];
    const full = reqs.filter((r) => r.status === "full").length;
    const partial = reqs.filter((r) => r.status === "partial").length;
    const not = reqs.filter((r) => r.status === "not_started" || !r.status).length;
    const total = reqs.length;
    const coverage = total ? Math.round((reqs.reduce((a, r) => a + (r.coverage || 0), 0) / total)) : 0;
    return { full, partial, not, total, coverage };
  }, [proposal]);

  const sections = useMemo(() => {
    const set = new Set();
    (proposal.requirements || []).forEach((r) => r.section && set.add(r.section));
    return Array.from(set);
  }, [proposal]);

  const filtered = (proposal.requirements || []).filter((r) => {
    if (statusFilter !== "all" && (r.status || "not_started") !== statusFilter) return false;
    if (sectionFilter !== "all" && r.section !== sectionFilter) return false;
    return true;
  });

  const downloadMatrix = () => exporter.matrixXlsx(proposal.id);

  return (
    <div className="flex flex-col" style={{ gap: 24 }} data-testid="compliance-panel">
      <div className="page-header">
        <div>
          <div className="page-title">Compliance Matrix</div>
          <div className="page-subtitle">
            {proposal.title} · {stats.total} requirements · {stats.coverage}% covered
          </div>
        </div>
        <div className="flex gap-8">
          <button className="btn btn-ghost btn-sm" onClick={downloadMatrix} data-testid="export-matrix-btn">
            📊 Export to Excel
          </button>
          <button className="btn btn-primary btn-sm" onClick={onEditResponse} data-testid="edit-response-btn">
            ✏️ Edit Response
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16 }}>
        <div className="stat-card green">
          <div className="stat-label">Fully Addressed</div>
          <div className="stat-value" style={{ fontSize: 26 }}>{stats.full}</div>
          <div className="stat-meta">{stats.total ? Math.round(stats.full * 100 / stats.total) : 0}% of requirements</div>
        </div>
        <div className="stat-card orange">
          <div className="stat-label">Partially Addressed</div>
          <div className="stat-value" style={{ fontSize: 26 }}>{stats.partial}</div>
          <div className="stat-meta">{stats.total ? Math.round(stats.partial * 100 / stats.total) : 0}% of requirements</div>
        </div>
        <div className="stat-card blue">
          <div className="stat-label">Not Started</div>
          <div className="stat-value" style={{ fontSize: 26 }}>{stats.not}</div>
          <div className="stat-meta">{stats.total ? Math.round(stats.not * 100 / stats.total) : 0}% of requirements</div>
        </div>
        <div className="stat-card purple">
          <div className="stat-label">AI Coverage Score</div>
          <div className="stat-value" style={{ fontSize: 26 }}>{stats.coverage}%</div>
          <div className="stat-meta">RAG context matched</div>
        </div>
      </div>

      <SkillVerification proposalId={proposal.id} />

      <div className="card">
        <div className="card-header">
          <span className="card-title">Requirements Shredding — All {stats.total} Requirements</span>
          <div className="flex gap-8">
            <select className="select-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter">
              <option value="all">All Status</option>
              <option value="full">Addressed</option>
              <option value="partial">Partial</option>
              <option value="not_started">Not Started</option>
            </select>
            <select className="select-filter" value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)} data-testid="section-filter">
              <option value="all">All Sections</option>
              {sections.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="scrollable">
          {filtered.length === 0 ? (
            <div className="empty-state">
              <div className="es-icon">📭</div>
              <div className="es-title">No requirements match these filters</div>
            </div>
          ) : (
            filtered.map((r) => {
              const sp = STATUS_PILL[r.status || "not_started"] || STATUS_PILL.not_started;
              return (
                <div className="req-row" key={r.id} data-testid={`req-${r.id}`} style={r.change_status === "removed" ? { opacity: 0.6 } : undefined}>
                  <div className="req-num">{r.id}</div>
                  <div className="req-text">
                    <div style={r.change_status === "removed" ? { textDecoration: "line-through" } : undefined}>
                      {CHANGE_BADGE[r.change_status] && (
                        <span className={`pill ${CHANGE_BADGE[r.change_status].c}`} style={{ marginRight: 6, fontSize: 10 }}>
                          {CHANGE_BADGE[r.change_status].t}
                        </span>
                      )}
                      {r.text}
                    </div>
                    {r.change_status === "modified" && r.amended_text && (
                      <div style={{ fontSize: 11, color: "var(--warning)", marginTop: 2 }}>Amended: {r.amended_text}</div>
                    )}
                    <div className="req-section">Section: {r.section || "—"} · {r.category}</div>
                  </div>
                  <span className="req-assignee">{r.assignee || "Not Assigned"}</span>
                  <div className="req-coverage">
                    <div style={{ fontSize: 10, color: covColor(r.coverage || 0), fontWeight: 700 }}>
                      {r.coverage || 0}%
                    </div>
                    <div className="coverage-bar">
                      <div className={`coverage-fill ${covCls(r.coverage || 0)}`} style={{ width: `${r.coverage || 0}%` }} />
                    </div>
                  </div>
                  <div className="req-actions">
                    <span className={`pill ${sp.cls}`}>{sp.label}</span>
                    <button
                      className={`btn ${r.status === "not_started" ? "btn-warning" : r.status === "partial" ? "btn-primary" : "btn-ghost"} btn-sm`}
                      onClick={onEditResponse}
                      data-testid={`req-action-${r.id}`}
                    >
                      {r.status === "not_started" ? "Assign" : r.status === "partial" ? "Complete" : "View"}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
